import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp, Mic, Camera, Target, Users, CreditCard, PiggyBank, BarChart3, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function HelpPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const content = {
    fr: {
      title: 'Aide',
      subtitle: 'Comment utiliser SpenSpot',
      features: [
        {
          icon: Mic,
          title: 'Saisie vocale',
          description: 'Dites simplement "15 euros au restaurant" ou "j\'ai dépensé 25 euros chez Carrefour" pour ajouter une dépense rapidement.'
        },
        {
          icon: Camera,
          title: 'Scanner de tickets',
          description: 'Prenez une photo de votre ticket de caisse pour extraire automatiquement le montant et les détails de vos achats.'
        },
        {
          icon: Target,
          title: 'Objectifs d\'épargne',
          description: 'Créez des objectifs d\'épargne et suivez votre progression. Ajoutez des contributions régulières pour atteindre vos buts.'
        },
        {
          icon: Users,
          title: 'Mode famille',
          description: 'Gérez les finances familiales ensemble. Partagez un pot commun, suivez les dépenses de chacun et configurez l\'argent de poche.'
        },
        {
          icon: CreditCard,
          title: 'Abonnements récurrents',
          description: 'Suivez tous vos abonnements (Netflix, Spotify, etc.) et recevez des alertes avant les renouvellements.'
        },
        {
          icon: BarChart3,
          title: 'Rapports et analyses',
          description: 'Visualisez vos dépenses par catégorie, comparez avec les mois précédents et identifiez vos habitudes de consommation.'
        }
      ],
      faq: [
        {
          question: 'Comment ajouter une dépense ?',
          answer: 'Cliquez sur le bouton "+" sur le tableau de bord. Vous pouvez saisir manuellement, utiliser la voix, ou scanner un ticket.'
        },
        {
          question: 'Comment créer une famille ?',
          answer: 'Allez dans l\'onglet Famille et cliquez sur "Créer une famille". Vous recevrez un code d\'invitation à partager avec les membres.'
        },
        {
          question: 'Comment fonctionne l\'argent de poche ?',
          answer: 'Les parents peuvent configurer un montant mensuel automatique pour chaque enfant. Les enfants peuvent faire des demandes que les parents approuvent.'
        },
        {
          question: 'Puis-je exporter mes données ?',
          answer: 'Oui, allez dans Paramètres > Exporter les données. Vous pouvez télécharger un fichier CSV de toutes vos transactions.'
        },
        {
          question: 'Comment changer de devise ?',
          answer: 'Allez dans Paramètres et sélectionnez votre devise préférée dans la section "Préférences".'
        }
      ],
      needHelp: 'Besoin d\'aide ?',
      contactSupport: 'Contactez le support'
    },
    en: {
      title: 'Help',
      subtitle: 'How to use SpenSpot',
      features: [
        {
          icon: Mic,
          title: 'Voice input',
          description: 'Simply say "15 dollars at the restaurant" or "I spent 25 dollars at Walmart" to quickly add an expense.'
        },
        {
          icon: Camera,
          title: 'Receipt scanner',
          description: 'Take a photo of your receipt to automatically extract the amount and details of your purchases.'
        },
        {
          icon: Target,
          title: 'Savings goals',
          description: 'Create savings goals and track your progress. Add regular contributions to reach your targets.'
        },
        {
          icon: Users,
          title: 'Family mode',
          description: 'Manage family finances together. Share a common pot, track everyone\'s spending, and set up allowances.'
        },
        {
          icon: CreditCard,
          title: 'Recurring subscriptions',
          description: 'Track all your subscriptions (Netflix, Spotify, etc.) and receive alerts before renewals.'
        },
        {
          icon: BarChart3,
          title: 'Reports and analytics',
          description: 'View your spending by category, compare with previous months, and identify your spending habits.'
        }
      ],
      faq: [
        {
          question: 'How do I add an expense?',
          answer: 'Click the "+" button on the dashboard. You can enter manually, use voice, or scan a receipt.'
        },
        {
          question: 'How do I create a family?',
          answer: 'Go to the Family tab and click "Create a family". You\'ll receive an invitation code to share with members.'
        },
        {
          question: 'How does pocket money work?',
          answer: 'Parents can set up an automatic monthly amount for each child. Children can make requests that parents approve.'
        },
        {
          question: 'Can I export my data?',
          answer: 'Yes, go to Settings > Export data. You can download a CSV file of all your transactions.'
        },
        {
          question: 'How do I change currency?',
          answer: 'Go to Settings and select your preferred currency in the "Preferences" section.'
        }
      ],
      needHelp: 'Need help?',
      contactSupport: 'Contact support'
    },
    pt: {
      title: 'Ajuda',
      subtitle: 'Como usar o SpenSpot',
      features: [
        {
          icon: Mic,
          title: 'Entrada por voz',
          description: 'Diga simplesmente "15 euros no restaurante" ou "gastei 25 euros no supermercado" para adicionar uma despesa rapidamente.'
        },
        {
          icon: Camera,
          title: 'Scanner de recibos',
          description: 'Tire uma foto do seu recibo para extrair automaticamente o valor e os detalhes das suas compras.'
        },
        {
          icon: Target,
          title: 'Objetivos de poupança',
          description: 'Crie objetivos de poupança e acompanhe o seu progresso. Adicione contribuições regulares para atingir os seus objetivos.'
        },
        {
          icon: Users,
          title: 'Modo família',
          description: 'Gira as finanças familiares em conjunto. Partilhe um fundo comum, acompanhe as despesas de cada um e configure a mesada.'
        },
        {
          icon: CreditCard,
          title: 'Assinaturas recorrentes',
          description: 'Acompanhe todas as suas assinaturas (Netflix, Spotify, etc.) e receba alertas antes das renovações.'
        },
        {
          icon: BarChart3,
          title: 'Relatórios e análises',
          description: 'Visualize as suas despesas por categoria, compare com meses anteriores e identifique os seus hábitos de consumo.'
        }
      ],
      faq: [
        {
          question: 'Como adiciono uma despesa?',
          answer: 'Clique no botão "+" no painel. Pode introduzir manualmente, usar a voz ou digitalizar um recibo.'
        },
        {
          question: 'Como crio uma família?',
          answer: 'Vá ao separador Família e clique em "Criar uma família". Receberá um código de convite para partilhar com os membros.'
        },
        {
          question: 'Como funciona a mesada?',
          answer: 'Os pais podem configurar um valor mensal automático para cada filho. Os filhos podem fazer pedidos que os pais aprovam.'
        },
        {
          question: 'Posso exportar os meus dados?',
          answer: 'Sim, vá a Definições > Exportar dados. Pode descarregar um ficheiro CSV de todas as suas transações.'
        },
        {
          question: 'Como mudo a moeda?',
          answer: 'Vá a Definições e selecione a sua moeda preferida na secção "Preferências".'
        }
      ],
      needHelp: 'Precisa de ajuda?',
      contactSupport: 'Contactar suporte'
    }
  };

  const c = content[language] || content.fr;

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b">
        <div className="flex items-center gap-3 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold">{c.title}</h1>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-8 max-w-2xl mx-auto">
        <motion.p 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-muted-foreground text-center"
        >
          {c.subtitle}
        </motion.p>

        {/* Features */}
        <div className="space-y-4">
          <h2 className="font-semibold text-lg">{language === 'fr' ? 'Fonctionnalités' : language === 'pt' ? 'Funcionalidades' : 'Features'}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {c.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="h-full">
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium mb-1">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="space-y-4">
          <h2 className="font-semibold text-lg">FAQ</h2>
          <div className="space-y-2">
            {c.faq.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 + index * 0.05 }}
              >
                <Card 
                  className="cursor-pointer overflow-hidden"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{item.question}</span>
                      {expandedFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <AnimatePresence>
                      {expandedFaq === index && (
                        <motion.p
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="text-sm text-muted-foreground mt-3 overflow-hidden"
                        >
                          {item.answer}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Contact Support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center space-y-3 pt-4"
        >
          <p className="text-muted-foreground">{c.needHelp}</p>
          <Button 
            onClick={() => navigate('/settings')}
            className="gap-2"
            data-testid="contact-support-btn"
          >
            <MessageCircle className="w-4 h-4" />
            {c.contactSupport}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
